import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface FAQItem {
  question: string;
  answer: string;
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface ServiceSchemaInput {
  name: string;
  serviceType: string;
  description?: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  image?: string;
  faq?: FAQItem[];
  noIndex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  service?: ServiceSchemaInput;
  includeWebsite?: boolean;
}

const SITE_URL = "https://www.gslimoelite.com";
const SITE_NAME = "GS Limo Elite";
const PHONE = "+1-929-452-6031";
const BUSINESS_ID = `${SITE_URL}/#business`;
const LOGO = `${SITE_URL}/images/gs-limo-elite-logo-dark-220-v7.webp`;

// City-level signals for local search. Kept in sync with src/data/serviceAreas.ts.
const AREA_CITIES = [
  "Glassboro",
  "Mullica Hill",
  "Swedesboro",
  "Woolwich Township",
  "West Deptford",
  "Woodbury",
  "Pitman",
  "Sewell",
  "Williamstown",
  "Deptford",
  "Cherry Hill",
  "Voorhees",
  "Berlin",
  "Sicklerville",
  "Vineland",
  "Philadelphia",
  "Haddonfield",
  "Mount Laurel",
  "Moorestown",
  "Marlton",
  "Atlantic City",
  "Princeton",
  "Trenton",
  "New Brunswick",
  "Newark",
  "Jersey City",
  "Hoboken",
  "King of Prussia",
  "Bensalem",
  "Media",
  "West Chester",
  "Doylestown",
  "Allentown",
  "New York",
  "Brooklyn",
  "Queens",
  "Staten Island",
  "Bronx",
  "White Plains",
];

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  image,
  faq,
  noIndex = false,
  breadcrumbs,
  service,
  includeWebsite = false,
}: SEOProps): JSX.Element {
  const { pathname } = useLocation();
  // Titles that already contain the site name are used verbatim to avoid duplication.
  const fullTitle = title
    ? title.includes(SITE_NAME)
      ? title
      : `${title} | ${SITE_NAME}`
    : `${SITE_NAME} – Luxury Transportation NJ, PA & NY`;
  const desc =
    description ||
    "Limo and black car service across NJ, PA & NY. Airport transfers to PHL, EWR & JFK, corporate travel, and special events. Call (929) 452-6031.";
  const kw =
    keywords ||
    "limo service NJ, luxury transportation, airport transfer, limousine rental, corporate travel NJ, black car service, sedan service, SUV service, sprinter van";
  const canonicalUrl = canonical
    ? canonical.startsWith("http")
      ? canonical
      : `${SITE_URL}${canonical}`
    : `${SITE_URL}${pathname === "/" ? "" : pathname}`;
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image}`
    : `${SITE_URL}/images/home-banner.webp`;

  const schema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "TaxiService"],
    "@id": BUSINESS_ID,
    name: SITE_NAME,
    alternateName: "GS Limousine Elite",
    telephone: PHONE,
    email: "info@gslimoelite.com",
    url: SITE_URL,
    logo: LOGO,
    image: [ogImage, LOGO],
    description: desc,
    geo: {
      "@type": "GeoCoordinates",
      latitude: 39.7029,
      longitude: -75.1118,
    },
    areaServed: [
      ...AREA_CITIES.map((city) => ({ "@type": "City", name: city })),
      { "@type": "State", name: "New Jersey" },
      { "@type": "State", name: "Pennsylvania" },
      { "@type": "State", name: "New York" },
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "328 Delsea Dr N #2",
      addressLocality: "Glassboro",
      addressRegion: "NJ",
      postalCode: "08028",
      addressCountry: "US",
    },
    currenciesAccepted: "USD",
    paymentAccepted: "Cash, Credit Card, Debit Card, Zelle, Venmo, Cash App, Apple Pay",
    // Availability and operating hours are business-owner facts; do not publish
    // an assumed 24/7 schedule in structured data.
    sameAs: [
      "https://web.facebook.com/people/GS-LIMO-ELITE/61585471506965/",
      "https://www.instagram.com/gslimoselite/",
      "https://www.yelp.com/biz/gs-limo-elite-glassboro-2",
      "https://www.tiktok.com/@gslimoelite",
    ],
    makesOffer: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Airport Transfers",
          description:
            "Reliable, tracked transfers to and from PHL, EWR, JFK, LGA and ACY.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Corporate Travel",
          description:
            "Punctual, professional transportation for meetings, executive travel, and client pickups.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Special Events & Weddings",
          description:
            "Transportation for bridal parties, milestone celebrations, and special occasions.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Group Transportation",
          description:
            "Sprinter vans for larger groups — bachelor/bachelorette parties, family trips, or corporate outings.",
        },
      },
    ],
  };

  // FAQPage schema — only emitted when a page passes in its Q&As.
  const faqSchema =
    faq && faq.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }
      : null;

  // BreadcrumbList — mirrors the visible breadcrumb trail on pages that render one.
  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.name,
            item: `${SITE_URL}${crumb.path === "/" ? "/" : crumb.path}`,
          })),
        }
      : null;

  const serviceSchema = service
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.name,
        serviceType: service.serviceType,
        description: service.description || desc,
        url: canonicalUrl,
        provider: { "@id": BUSINESS_ID },
        areaServed: breadcrumbs && breadcrumbs.length > 0
          ? { "@type": "City", name: breadcrumbs[breadcrumbs.length - 1].name.replace(/ Limo Service.*$/, "") }
          : AREA_CITIES.map((city) => ({ "@type": "City", name: city })),
      }
    : null;

  const websiteSchema = includeWebsite
    ? {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: SITE_NAME,
        publisher: { "@id": BUSINESS_ID },
        inLanguage: "en-US",
      }
    : null;

  return (
    // defer={false}: react-helmet-async otherwise schedules head updates on
    // requestAnimationFrame. react-snap serialises the DOM after a fixed wait,
    // and in headless Chrome that frame often never fires, which left some
    // prerendered routes with the template title and no description/JSON-LD.
    // Applying synchronously guarantees every snap captures the real head.
    <Helmet defer={false}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <meta
        name="robots"
        content={
          noIndex
            ? "noindex, follow"
            : "index, follow, max-image-preview:large, max-snippet:-1"
        }
      />
      <meta name="author" content={SITE_NAME} />
      <meta name="geo.region" content="US-NJ" />
      <meta name="geo.placename" content="Glassboro" />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} — chauffeured limo and black car service`} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* JSON-LD Schema */}
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}
      {websiteSchema && (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      )}
    </Helmet>
  );
}
